import React, { useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "../ui/tooltip";
import {
	HoverCard,
	HoverCardTrigger,
	HoverCardContent,
} from "../ui/hover-card";
import { Skeleton } from "../ui/skeleton";
import { JobRequestMessageV1Block } from "../chat/message";
import { JobRequestCustomV1Block } from "../chat/messages/job-request-custom/job-request-custom";
import { supabase } from "@/lib/supabase";
import {
	MessageType,
	JobRequestMessageV1,
	JobRequestCustomV1,
} from "@/lib/store/Realtime/message.types";

interface JobRequestItemProps {
	channelId: string;
	title: string | null;
	customerName?: string;
	onAccept: (channelId: string) => void;
	onDecline: (channelId: string) => void;
	onClick?: () => void;
}

const JobRequestPreview: React.FC<{ channelId: string }> = ({ channelId }) => {
	const [message, setMessage] = useState<
		JobRequestMessageV1 | JobRequestCustomV1 | null
	>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let cancelled = false;
		const fetchMessage = async () => {
			const { data } = await supabase
				.from("messages")
				.select("content")
				.eq("channel_id", channelId)
				.order("created_at", { ascending: true })
				.limit(1);

			const content = data?.[0]?.content as unknown as
				| JobRequestMessageV1
				| JobRequestCustomV1
				| null;
			const isJobRequest =
				content?.type === MessageType.JOB_REQUEST_V1 ||
				content?.type === MessageType.JOB_REQUEST_CUSTOM_V1;

			if (!cancelled) {
				if (content && isJobRequest) setMessage(content);
				setLoading(false);
			}
		};
		fetchMessage();
		return () => {
			cancelled = true;
		};
	}, [channelId]);

	if (loading) {
		return (
			<div className="space-y-2">
				<Skeleton className="h-4 w-3/4" />
				<Skeleton className="h-4 w-1/2" />
				<Skeleton className="h-16 w-full" />
			</div>
		);
	}

	if (!message) {
		return (
			<p className="text-xs text-gray-400">Nachricht nicht verfügbar</p>
		);
	}

	if (message.type === MessageType.JOB_REQUEST_V1) {
		return (
			<JobRequestMessageV1Block
				content={message}
				messageOfCurrentOrg={false}
				onShareFile={() => {}}
				onUnshareFile={() => {}}
				isAdmin={false}
			/>
		);
	}

	return (
		<JobRequestCustomV1Block
			content={message}
			messageOfCurrentOrg={false}
			onShareFile={() => {}}
			onUnshareFile={() => {}}
			isAdmin={false}
		/>
	);
};

export const JobRequestItem: React.FC<JobRequestItemProps> = ({
	channelId,
	title,
	customerName,
	onAccept,
	onDecline,
	onClick,
}) => {
	const row = (
		<div
			className={`px-3 py-2 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 flex items-center gap-2${
				onClick ? " cursor-pointer" : ""
			}`}
			onClick={onClick}
			data-testid={`inbox-job-request-${channelId}`}
			aria-label={`Auftragsanfrage: ${title || "Neue Auftragsanfrage"}`}
		>
			<div className="flex-1 min-w-0">
				<p className="text-sm font-semibold text-gray-900 truncate">
					{title || "Neue Auftragsanfrage"}
				</p>
				{customerName && (
					<p className="text-[13px] text-gray-500 truncate">
						{customerName}
					</p>
				)}
			</div>
			<div className="flex items-center gap-1 flex-shrink-0">
				<Tooltip>
					<TooltipTrigger asChild>
						<button
							className="h-7 w-7 flex items-center justify-center rounded text-emerald-600 hover:bg-emerald-50 transition-colors"
							onClick={(e) => {
								e.stopPropagation();
								onAccept(channelId);
							}}
							aria-label="Auftragsanfrage annehmen"
							data-testid={`job-request-accept-${channelId}`}
						>
							<Check className="h-4 w-4" />
						</button>
					</TooltipTrigger>
					<TooltipContent side="top">Annehmen</TooltipContent>
				</Tooltip>
				<Tooltip>
					<TooltipTrigger asChild>
						<button
							className="h-7 w-7 flex items-center justify-center rounded text-red-500 hover:bg-red-50 transition-colors"
							onClick={(e) => {
								e.stopPropagation();
								onDecline(channelId);
							}}
							aria-label="Auftragsanfrage ablehnen"
							data-testid={`job-request-decline-${channelId}`}
						>
							<X className="h-4 w-4" />
						</button>
					</TooltipTrigger>
					<TooltipContent side="top">Ablehnen</TooltipContent>
				</Tooltip>
			</div>
		</div>
	);

	return (
		<HoverCard openDelay={500} closeDelay={100}>
			<HoverCardTrigger asChild>{row}</HoverCardTrigger>
			<HoverCardContent
				side="right"
				className="w-96 max-h-[28rem] overflow-y-auto p-0"
			>
				<div className="p-3">
					<JobRequestPreview channelId={channelId} />
				</div>
				<div className="sticky bottom-0 border-t border-gray-200 bg-white p-2 flex gap-2">
					<button
						className="flex-1 px-3 py-1.5 text-sm font-medium rounded bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
						onClick={() => onAccept(channelId)}
						data-testid={`job-request-hover-accept-${channelId}`}
					>
						Akzeptieren
					</button>
					<button
						className="flex-1 px-3 py-1.5 text-sm font-medium rounded bg-red-500 text-white hover:bg-red-600 transition-colors"
						onClick={() => onDecline(channelId)}
						data-testid={`job-request-hover-decline-${channelId}`}
					>
						Ablehnen
					</button>
				</div>
			</HoverCardContent>
		</HoverCard>
	);
};
